import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Navbar.css';

const iconProps = { width: 22, height: 22, strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };

const NavIcons = {
  about: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
  home: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  standings: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" />
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20 17 22" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2z" />
    </svg>
  ),
  bets: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  ),
  leagues: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  store: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  ),
  logout: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
  account: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )
};

const Navbar = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasInvitePip = Boolean(user && (user.pendingInviteCount || 0) > 0);

  useEffect(() => {
    if (!user) return undefined;

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshUser();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [user, refreshUser]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav className="sc-navbar">
        <div className="sc-navbar-inner">
          <Link to="/" className="sc-navbar-brand">
            <img src="/logo.svg" alt="ScoreClash" className="sc-logo" />
            <span><span className="sc-brand-score">Score</span><span className="sc-brand-clash">Clash</span></span>
          </Link>
          <div className="sc-navbar-desktop">
            <Link to="/" className={`sc-nav-link ${isActive('/') ? 'active' : ''}`}>Home</Link>
            <Link to="/standings" className={`sc-nav-link ${isActive('/standings') ? 'active' : ''}`}>Standings</Link>
            {user ? (
              <>
                <Link to="/picks" className={`sc-nav-link ${isActive('/picks') ? 'active' : ''}`}>Picks</Link>
                <Link
                  to="/leagues"
                  className={`sc-nav-link sc-nav-link--pip-wrap ${isActive('/leagues') ? 'active' : ''}`}
                  aria-label={hasInvitePip ? 'Leagues, pending invitation' : 'Leagues'}
                >
                  Leagues
                  {hasInvitePip ? <span className="sc-nav-pip" aria-hidden="true" /> : null}
                </Link>
                <Link to="/store" className={`sc-nav-link ${isActive('/store') ? 'active' : ''}`}>Store</Link>
                <Link to="/about" className={`sc-nav-link ${isActive('/about') ? 'active' : ''}`}>About</Link>
                <span className="sc-nav-user">
                  <span className="sc-nav-coins" title="Your coin balance">
                    <img src="/coin.svg" alt="" className="sc-nav-coin-icon" />
                    <span>{user.coinBalance ?? 0}</span>
                  </span>
                  <Link to="/account" className="sc-nav-account-link">
                    {user.username || user.email}
                  </Link>
                </span>
                <button onClick={handleLogout} className="sc-btn sc-btn-secondary sc-btn-sm">Logout</button>
              </>
            ) : (
              <>
                <Link to="/about" className={`sc-nav-link ${isActive('/about') ? 'active' : ''}`}>About</Link>
                <Link to="/login" className="sc-nav-link">Login</Link>
                <Link to="/register" className="sc-btn sc-btn-primary sc-btn-sm">Join</Link>
              </>
            )}
          </div>
          <div className="sc-navbar-mobile-actions">
            {user ? (
              <>
                <span className="sc-nav-coins sc-nav-coins-mobile" title="Your coin balance">
                  <img src="/coin.svg" alt="" className="sc-nav-coin-icon" />
                  <span>{user.coinBalance ?? 0}</span>
                </span>
                <Link
                  to="/account"
                  className={`sc-nav-mobile-account ${isActive('/account') ? 'active' : ''}`}
                  title="Account"
                  aria-label="Account"
                >
                  <span className="sc-nav-icon">{NavIcons.account}</span>
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" className="sc-btn sc-btn-secondary sc-btn-sm">Login</Link>
                <Link to="/register" className="sc-btn sc-btn-primary sc-btn-sm">Join</Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {user && (
        <nav className="sc-bottom-nav">
          <Link to="/" className={`sc-bottom-nav-item ${isActive('/') ? 'active' : ''}`}>
            <span className="sc-bottom-nav-icon">{NavIcons.home}</span>
            <span>Home</span>
          </Link>
          <Link to="/standings" className={`sc-bottom-nav-item ${isActive('/standings') ? 'active' : ''}`}>
            <span className="sc-bottom-nav-icon">{NavIcons.standings}</span>
            <span>Standings</span>
          </Link>
          <Link to="/picks" className={`sc-bottom-nav-item ${isActive('/picks') ? 'active' : ''}`}>
            <span className="sc-bottom-nav-icon">{NavIcons.bets}</span>
            <span>Picks</span>
          </Link>
          <Link
            to="/leagues"
            className={`sc-bottom-nav-item sc-bottom-nav-item--pip-wrap ${isActive('/leagues') ? 'active' : ''}`}
            aria-label={hasInvitePip ? 'Leagues, pending invitation' : 'Leagues'}
          >
            <span className="sc-bottom-nav-icon sc-bottom-nav-icon--pip">
              {NavIcons.leagues}
              {hasInvitePip ? <span className="sc-nav-pip sc-nav-pip--icon" aria-hidden="true" /> : null}
            </span>
            <span>Leagues</span>
          </Link>
          <Link to="/store" className={`sc-bottom-nav-item ${isActive('/store') ? 'active' : ''}`}>
            <span className="sc-bottom-nav-icon">{NavIcons.store}</span>
            <span>Store</span>
          </Link>
        </nav>
      )}
    </>
  );
};

export default Navbar;
